/**
 * Slack Configuration Dialog Component
 *
 * Modal dialog for managing the organization's Slack integration.
 * States: not configured (setup form), configured (view), editing, testing connection.
 * Follows the JiraConfigDialog pattern.
 */

import { ReactNode, useState, useEffect } from "react";
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  useSlackIntegration,
  useCreateSlackIntegration,
  useUpdateSlackIntegration,
  useDeleteSlackIntegration,
  useTestSlackConnection,
} from "@/hooks/useSlackIntegration";
import { useOrganizationContext } from "@/providers/OrganizationProvider";
import type { SlackIntegrationCreate } from "@/types/slackIntegration";

interface SlackConfigDialogProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

type DialogView = "view" | "setup" | "edit" | "confirm-delete";

export function SlackConfigDialog({ children, open, onOpenChange }: SlackConfigDialogProps) {
  const { data: slackIntegration, isLoading } = useSlackIntegration();
  const { isOrganizationOwner } = useOrganizationContext();

  const createMutation = useCreateSlackIntegration();
  const updateMutation = useUpdateSlackIntegration();
  const deleteMutation = useDeleteSlackIntegration();
  const testMutation = useTestSlackConnection();

  const [view, setView] = useState<DialogView>("view");
  const [showWebhook, setShowWebhook] = useState(false);
  const [formData, setFormData] = useState<SlackIntegrationCreate>({
    webhook_url: "",
    channel_name: "",
    workspace_name: "",
  });
  const [testPassed, setTestPassed] = useState(false);

  const isConfigured = slackIntegration != null;

  // Set the correct initial view when the dialog opens or data changes
  useEffect(() => {
    if (open) {
      if (isConfigured) {
        setView("view");
      } else {
        setView("setup");
      }
      resetForm();
    }
  }, [open, isConfigured]);

  function resetForm() {
    setFormData({
      webhook_url: "",
      channel_name: "",
      workspace_name: "",
    });
    setTestPassed(false);
    setShowWebhook(false);
    testMutation.reset();
    createMutation.reset();
    updateMutation.reset();
  }

  function handleStartEdit() {
    if (!slackIntegration) return;
    setFormData({
      webhook_url: "", // user must re-enter webhook to change it
      channel_name: slackIntegration.channel_name ?? "",
      workspace_name: slackIntegration.workspace_name ?? "",
    });
    setTestPassed(false);
    testMutation.reset();
    setView("edit");
  }

  async function handleTestConnection() {
    testMutation.reset();
    setTestPassed(false);
    try {
      const trimmedUrl = formData.webhook_url.trim();
      const credentials =
        view === "setup" || (view === "edit" && trimmedUrl)
          ? { webhook_url: trimmedUrl }
          : undefined;

      const result = await testMutation.mutateAsync(credentials);
      if (result.success) {
        setTestPassed(true);
      }
    } catch {
      // Error handled by mutation state
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();

    try {
      if (view === "setup") {
        if (!formData.webhook_url.trim()) return;
        await createMutation.mutateAsync({
          webhook_url: formData.webhook_url.trim(),
          channel_name: formData.channel_name?.trim() || undefined,
          workspace_name: formData.workspace_name?.trim() || undefined,
        });
      } else {
        // Edit mode — only send fields that changed
        const updates: Record<string, string | boolean | null | undefined> = {};
        if (formData.webhook_url.trim()) {
          updates.webhook_url = formData.webhook_url.trim();
        }
        const newChannelName = formData.channel_name?.trim() || null;
        if (newChannelName !== (slackIntegration?.channel_name ?? null)) {
          updates.channel_name = newChannelName;
        }
        const newWorkspaceName = formData.workspace_name?.trim() || null;
        if (newWorkspaceName !== (slackIntegration?.workspace_name ?? null)) {
          updates.workspace_name = newWorkspaceName;
        }
        await updateMutation.mutateAsync(updates);
      }
      setView("view");
      resetForm();
    } catch {
      // Error handled by mutation state
    }
  }

  async function handleDelete() {
    try {
      await deleteMutation.mutateAsync();
      setView("setup");
      resetForm();
    } catch {
      // Error handled by mutation state
    }
  }

  function handleCancel() {
    if (isConfigured) {
      setView("view");
    }
    resetForm();
    testMutation.reset();
    createMutation.reset();
    updateMutation.reset();
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const saveError = createMutation.error || updateMutation.error;
  const isFormValid =
    view === "edit" || formData.webhook_url.trim() !== "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Slack Integration</DialogTitle>
          <DialogDescription>
            Connect Slack to receive alert notifications via webhook
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : view === "confirm-delete" ? (
          /* Delete Confirmation */
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Delete Slack Integration?</p>
                <p className="text-xs mt-1">
                  This will remove the Slack connection for your organization.
                  Alerts using the organization Slack channel will stop sending
                  notifications until reconfigured.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setView("view")}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete Integration"
                )}
              </Button>
            </div>
          </div>
        ) : view === "view" && isConfigured ? (
          /* Configured View */
          <div className="space-y-4">
            {/* Status Badge */}
            <div className="flex items-center gap-2">
              {slackIntegration.is_active ? (
                <Badge className="bg-green-600 hover:bg-green-700 text-xs">
                  Connected
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-xs">
                  Inactive
                </Badge>
              )}
            </div>

            {/* Error warnings */}
            {slackIntegration.last_error && (
              <div className="flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-xs">Last error: {slackIntegration.last_error}</p>
                  {slackIntegration.consecutive_errors > 3 && (
                    <p className="text-xs mt-1">
                      Integration may need attention ({slackIntegration.consecutive_errors} consecutive errors).
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Integration Details */}
            <div className="space-y-3">
              <div className="grid grid-cols-[120px_1fr] gap-y-2 text-sm">
                <span className="text-muted-foreground">Webhook URL</span>
                <span className="font-medium font-mono">
                  {slackIntegration.webhook_url_last_four
                    ? `\u2022\u2022\u2022\u2022${slackIntegration.webhook_url_last_four}`
                    : "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}
                </span>

                {slackIntegration.channel_name && (
                  <>
                    <span className="text-muted-foreground">Channel</span>
                    <span className="font-medium">#{slackIntegration.channel_name}</span>
                  </>
                )}

                {slackIntegration.workspace_name && (
                  <>
                    <span className="text-muted-foreground">Workspace</span>
                    <span className="font-medium">{slackIntegration.workspace_name}</span>
                  </>
                )}

                {slackIntegration.last_used_at && (
                  <>
                    <span className="text-muted-foreground">Last Used</span>
                    <span className="font-medium">
                      {new Date(slackIntegration.last_used_at).toLocaleString()}
                    </span>
                  </>
                )}
              </div>
            </div>

            <Separator />

            {/* Actions */}
            {isOrganizationOwner && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleStartEdit} className="flex-1">
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setView("confirm-delete")}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            )}
            {!isOrganizationOwner && (
              <p className="text-xs text-muted-foreground">
                Only the organization owner can edit or delete this integration.
              </p>
            )}
          </div>
        ) : (
          /* Setup / Edit Form */
          <form onSubmit={handleSave} className="space-y-4">
            <h3 className="text-sm font-medium">
              {view === "edit" ? "Edit Integration" : "Set Up Slack Integration"}
            </h3>

            {/* Test Result: Success */}
            {testMutation.isSuccess && testMutation.data?.success && (
              <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
                <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Connection successful!</p>
                  <p className="text-xs mt-1">{testMutation.data.message}</p>
                </div>
              </div>
            )}

            {/* Test Result: Failed */}
            {testMutation.isSuccess && !testMutation.data?.success && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Connection test failed</p>
                  <p className="text-xs mt-1">{testMutation.data?.message}</p>
                </div>
              </div>
            )}

            {/* Test Result: Error */}
            {testMutation.isError && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Connection test failed</p>
                  <p className="text-xs mt-1">
                    {testMutation.error instanceof Error
                      ? testMutation.error.message
                      : "Unable to test connection"}
                  </p>
                </div>
              </div>
            )}

            {/* Save Error */}
            {saveError && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Failed to save</p>
                  <p className="text-xs mt-1">
                    {saveError instanceof Error ? saveError.message : "Unable to save integration"}
                  </p>
                </div>
              </div>
            )}

            {/* Webhook URL */}
            <div className="space-y-2">
              <Label htmlFor="slack-webhook-url">
                Webhook URL{" "}
                {view === "setup" && <span className="text-destructive">*</span>}
                {view === "edit" && (
                  <span className="text-xs text-muted-foreground font-normal">
                    (leave blank to keep current URL)
                  </span>
                )}
              </Label>
              <div className="relative">
                <Input
                  id="slack-webhook-url"
                  type={showWebhook ? "text" : "password"}
                  placeholder={
                    view === "edit"
                      ? "Enter new webhook URL to update"
                      : "https://hooks.slack.com/services/..."
                  }
                  value={formData.webhook_url}
                  onChange={(e) =>
                    setFormData({ ...formData, webhook_url: e.target.value })
                  }
                  autoComplete="off"
                  required={view === "setup"}
                  disabled={isSaving}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowWebhook(!showWebhook)}
                  aria-label={showWebhook ? "Hide webhook URL" : "Show webhook URL"}
                >
                  {showWebhook ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Create an Incoming Webhook in your Slack workspace under{" "}
                <span className="font-medium">Apps &gt; Incoming Webhooks</span>.
              </p>
            </div>

            {/* Channel Name */}
            <div className="space-y-2">
              <Label htmlFor="slack-channel-name">Channel Name (optional)</Label>
              <Input
                id="slack-channel-name"
                placeholder="e.g. security-alerts"
                value={formData.channel_name ?? ""}
                onChange={(e) =>
                  setFormData({ ...formData, channel_name: e.target.value })
                }
                autoComplete="off"
                disabled={isSaving}
              />
              <p className="text-xs text-muted-foreground">
                For display purposes only. The channel is determined by the webhook.
              </p>
            </div>

            {/* Workspace Name */}
            <div className="space-y-2">
              <Label htmlFor="slack-workspace-name">Workspace Name (optional)</Label>
              <Input
                id="slack-workspace-name"
                placeholder="e.g. My Company"
                value={formData.workspace_name ?? ""}
                onChange={(e) =>
                  setFormData({ ...formData, workspace_name: e.target.value })
                }
                autoComplete="off"
                disabled={isSaving}
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-2">
              {(view === "edit" || isConfigured) && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={handleTestConnection}
                disabled={
                  (!formData.webhook_url.trim() && view === "setup") ||
                  testMutation.isPending ||
                  isSaving
                }
              >
                {testMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  "Test Connection"
                )}
              </Button>
              <Button
                type="submit"
                disabled={!isFormValid || isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </div>

            {/* Warning if saving without testing */}
            {!testPassed && isFormValid && !testMutation.isPending && (
              <p className="text-xs text-muted-foreground">
                Tip: Test the connection before saving to verify your webhook URL.
              </p>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
